/**
 * Consentimiento biométrico Ley 21.719 — FUENTE ÚNICA VERSIONADA.
 *
 * Ninguna pantalla debe redactar de nuevo el texto del consentimiento.
 * Cualquier bloque en UI que solicite consentimiento biométrico debe
 * importar `BiometricConsentBlock` desde este módulo y NUNCA duplicar el
 * texto. La versión `BIOMETRIC_CONSENT_VERSION` viaja al backend y queda
 * persistida junto al aceptante — si el texto cambia, se debe subir la
 * versión (v3 → v4 → …) para poder demostrar qué texto vio cada usuario.
 *
 * Historial:
 *  - v2-2026-07 (deprecated): dos redacciones divergentes en RegisterNew
 *    y KycSelfService. Unificado en v3.
 *  - v3-2026-07 (actual): fuente única, español de Chile, refleja plazo
 *    real de conservación (última operación o término de relación
 *    comercial, lo que sea posterior) y consecuencia de revocar
 *    (cuenta bloqueada para operar, se debe rehacer verificación).
 */
import React from 'react';

export const BIOMETRIC_CONSENT_VERSION = 'v3-2026-07';

/**
 * Bloque de consentimiento para pantallas de captura (registro / KYC self-service).
 *
 * Props:
 *   checked (bool), onChange (fn), disabled (bool opcional).
 */
export function BiometricConsentBlock({ checked, onChange, disabled = false }) {
  return (
    <div
      className="bg-slate-50 border border-slate-200 rounded-xl p-4"
      data-testid="biometric-consent-block"
    >
      <p className="text-xs font-bold text-slate-700 mb-2 uppercase tracking-wide">
        Consentimiento para datos biométricos (Ley 21.719)
      </p>
      <p className="text-xs text-gray-600 mb-2">
        En el próximo paso te tomaremos una <strong>selfie</strong> que
        comparamos con tu documento para verificar tu identidad. Ese cotejo
        genera un <strong>dato biométrico</strong> y, por Ley 21.719,
        requiere tu autorización explícita, aparte de los Términos.
      </p>
      <ul className="text-[11px] text-gray-500 mb-2 list-disc pl-5 space-y-0.5">
        <li>Uso: verificar tu identidad y prevenir fraude o suplantación.</li>
        <li>
          Puedes <strong>revocar</strong> este consentimiento cuando quieras
          desde <a
            href="/mi-biometria"
            className="text-green-700 underline"
            onClick={(e) => e.stopPropagation()}
          >tu perfil → Datos biométricos</a>. Al revocarlo dejamos de usar tu biometría de inmediato.
        </li>
        <li>
          <strong>Al revocar tu cuenta queda bloqueada para operar.</strong>
          {' '}Para volver a enviar dinero deberás rehacer la verificación de
          identidad y otorgar consentimiento otra vez.
        </li>
        <li>
          <strong>Conservación distinta a eliminación:</strong> aunque
          revoques, la Ley 19.913 (UAF Chile) obliga a conservar los datos
          por <strong>5 años desde la última operación o desde el término
          de la relación comercial, lo que sea posterior</strong>. En ese
          período tu biometría queda <strong>bloqueada</strong>: nadie de
          la plataforma puede usarla operativamente y sólo se accedería
          ante un requerimiento judicial o regulatorio, con registro
          auditado.
        </li>
        <li>Al vencer el plazo de conservación, los datos se eliminan o anonimizan.</li>
        <li>
          Versión del texto aceptado:{' '}
          <code className="text-[10px]">{BIOMETRIC_CONSENT_VERSION}</code>
          {' '}· queda registrada con tu usuario.
        </li>
      </ul>
      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={!!checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-0.5 w-5 h-5 text-green-600 rounded border-2 border-gray-300 focus:ring-green-500"
          data-testid="biometric-consent-checkbox"
        />
        <span className="text-sm text-gray-800 font-semibold">
          Otorgo mi consentimiento expreso, específico e informado para el
          tratamiento de mis datos biométricos con las finalidades descritas.
        </span>
      </label>
    </div>
  );
}

/**
 * Panel de "qué pasa cuando revoco" que se muestra ANTES de confirmar
 * la revocación (en /mi-biometria y en /eliminar-cuenta). Redactado con
 * la consecuencia operativa explícita.
 */
export function BiometricRevokeConsequences({ brandName }) {
  return (
    <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1">
      <li>
        Dejamos de usar tu selfie/foto en verificación desde el momento en
        que confirmas la revocación.
      </li>
      <li>
        <strong>Tu cuenta queda bloqueada para operar.</strong> No podrás
        crear nuevas órdenes hasta que rehagas la verificación de identidad
        y otorgues consentimiento biométrico otra vez.
      </li>
      <li>
        Tu biometría queda <strong>bloqueada</strong>, no eliminada: por Ley
        19.913 (UAF Chile) debemos conservarla hasta <strong>5 años desde
        tu última operación o desde el término de la relación comercial,
        lo que sea posterior</strong>.
      </li>
      <li>
        Durante ese período nadie de {brandName} puede usarla
        operativamente; sólo se accede ante requerimiento judicial o
        regulatorio, con registro auditado.
      </li>
      <li>Al vencer el plazo, los datos se eliminan o anonimizan.</li>
    </ul>
  );
}
